import { useCallback } from 'react';

import {
  Alert,
  Box,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useGetDailyKpiSummary, useGetProductList } from 'src/api/admin';

import { IListFilters, IOrderProductItem, IProductTableFilters } from 'src/types';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { fCurrency } from 'src/utils/format-number';
import { toFiniteNumber } from 'src/utils/strict-number';
import { useLocales } from 'src/locales';
import { StatsExportButton } from 'src/components/stats-export';

import { slowStockWorkbook } from './slow-stock-export';
import { valueAtCost } from './slow-stock-value';

/**
 * NAT-2522 — how much capital is sitting in slow stock, and in which wines.
 *
 * Three sources, each loading and failing on its own:
 *
 * - The capital figure is the daily KPI's `capitalTiedUp120`: per product, stock aged FIFO from
 *   producer-order arrivals against live warehouse stock, minus bottles on Ready-for-delivery,
 *   unshipped orders, at cost (NAT-2525).
 * - The zero-sales count is the same KPI's `zeroSales60`: in-stock products with no real sale in
 *   60 days, the one place this number is defined (NAT-2525). It used to be read from the
 *   product list's `sku_in_stock` counter instead, on the claim that the two agreed — they read
 *   different populations (the list defaults to active products) and the KPI is the corrected,
 *   single source now.
 * - The list is the same product list with both filters: stock held since 120 days ago (the
 *   same FIFO-from-arrivals method `capitalTiedUp120` uses) and no sale in 60. The dedicated
 *   slow-moving-wines endpoint, which was meant for this, dated stock by `updated_at` and
 *   returned nothing (NAT-2591, removed along with its unused client wrapper).
 *
 * Both filters use the real sale definition (drafts, carts, tastings and internal orders do
 * not count) as of NAT-2525.
 */

const NOT_AVAILABLE = '—';

/** How many slow movers the table lists. The count card carries the total. */
export const SLOW_LIST_SIZE = 10;

/** What makes a wine a slow mover, and the list's order — shared by the screen and the export. */
const SLOW_MOVERS_CRITERIA: IListFilters<IProductTableFilters> = {
  stockAge: '120',
  lastSale: '60',
  // A preference, not a requirement, since NAT-2525 (#2003) fixed the missing-sortBy failure —
  // kept explicit so the list stays ordered by stock value.
  sortBy: 'stock_value',
  sortDirection: 'desc',
};

const SLOW_MOVERS_PARAMS: IListFilters<IProductTableFilters> = {
  ...SLOW_MOVERS_CRITERIA,
  page: 1,
  limit: SLOW_LIST_SIZE,
};

type Card = {
  key: string;
  label: string;
  value: string;
  hint: string;
};

function SubHeading({ children }: { children: string }) {
  return (
    <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

function CardGrid({ cards }: { cards: Card[] }) {
  const { t } = useLocales('slowStockDashboard');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: `repeat(${cards.length}, 1fr)` },
        gap: 1.5,
      }}
    >
      {cards.map((card) => (
        <Box key={card.key} sx={{ backgroundColor: 'background.neutral', borderRadius: 1, p: 3 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {card.label}
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 'fontWeightMedium' }}
            // A bare em-dash is not announced at default punctuation levels.
            aria-label={card.value === NOT_AVAILABLE ? t('non_disponibile') : undefined}
          >
            {card.value}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {card.hint}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CardSkeleton() {
  return <Skeleton variant="rounded" height={112} />;
}

type Props = {
  /** The page (and section) the list sits on, in the viewer's language: the export's title. */
  exportTitle: string;
};

export default function SlowStockSection({ exportTitle }: Props) {
  const { t, currentLang } = useLocales('slowStockDashboard');
  const { t: tCommon } = useLocales();
  const lang = currentLang.value;
  const numberFormat = new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'de-DE', {
    maximumFractionDigits: 0,
  });

  const kpi = useGetDailyKpiSummary();
  const slowMovers = useGetProductList(SLOW_MOVERS_PARAMS);

  const count = (value: number | null | undefined) =>
    value === null || value === undefined || Number.isNaN(value)
      ? NOT_AVAILABLE
      : numberFormat.format(value);
  const money = (value: number | null) =>
    value === null ? NOT_AVAILABLE : fCurrency(Math.round(value), lang);

  const capital = kpi.data?.capitalTiedUp120;
  // `Number(capital.value) || 0` folded a missing or non-numeric figure into a reportable €0 —
  // the strict parser keeps "unknown" as null instead, so the card renders "—".
  const capitalValue = capital ? toFiniteNumber(capital.value) : null;
  const capitalBottles = capital ? toFiniteNumber(capital.bottles) : null;
  const slowRows = slowMovers.data?.data ?? [];
  const slowTotal = slowMovers.data?.total_rows;

  // NAT-2481 — every slow mover, not the ten listed: the list's own request without the page,
  // which the product list answers in full with `all=true`.
  const buildExport = useCallback(async () => {
    const {
      data: { data },
    } = await axiosInstance.get<{ data?: { data?: IOrderProductItem[] } }>(
      endpoints.admin.products.root,
      { params: { ...SLOW_MOVERS_CRITERIA, all: true } },
    );
    return slowStockWorkbook({
      rows: data?.data ?? [],
      page: exportTitle,
      shownTop: SLOW_LIST_SIZE,
      t: (k, o) => (k === 'export_esportato_il' ? tCommon(k, o) : t(k, o)),
    });
  }, [exportTitle, t, tCommon]);

  const cards: { card: Card; loading: boolean; failed: boolean }[] = [
    {
      card: {
        key: 'capital',
        label: t('capitale_label'),
        value: money(capitalValue),
        hint: t('capitale_hint', { bottles: count(capitalBottles) }),
      },
      loading: kpi.isLoading && !kpi.data,
      failed: Boolean(kpi.error) && !kpi.data,
    },
    {
      card: {
        key: 'zero-sales',
        label: t('zero_vendite_label'),
        value: count(kpi.data?.zeroSales60?.count),
        hint: t('zero_vendite_hint'),
      },
      loading: kpi.isLoading && !kpi.data,
      failed: Boolean(kpi.error) && !kpi.data,
    },
    {
      card: {
        key: 'slow',
        label: t('lenti_label'),
        value: count(slowTotal),
        hint: t('lenti_hint'),
      },
      loading: slowMovers.isLoading && !slowMovers.data,
      failed: Boolean(slowMovers.error) && !slowMovers.data,
    },
  ];

  const renderCards = () => {
    if (cards.some((entry) => entry.loading)) {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: `repeat(${cards.length}, 1fr)` },
            gap: 1.5,
          }}
        >
          {cards.map((entry) => (
            <CardSkeleton key={entry.card.key} />
          ))}
        </Box>
      );
    }
    // A failed source shows its dash; the other figures stay readable.
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {cards.some((entry) => entry.failed) && (
          <Alert severity="error">{t('caricamento_non_riuscito')}</Alert>
        )}
        <CardGrid cards={cards.map((entry) => entry.card)} />
      </Box>
    );
  };

  const renderList = () => {
    if (slowMovers.isLoading && !slowMovers.data) {
      return <Skeleton variant="rounded" height={240} />;
    }
    if (slowMovers.error && !slowMovers.data) {
      return <Alert severity="error">{t('caricamento_non_riuscito')}</Alert>;
    }
    if (!slowRows.length) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('lista_vuota')}
        </Typography>
      );
    }
    // The value shown next to the list is a sum of `valueAtCost` over the LISTED rows only —
    // say so, and say when some of them had no cost or quantity to value, rather than quietly
    // reading a partial sum as the whole thing.
    const valueTotal = slowRows.reduce(
      (acc, row) => {
        const value = valueAtCost(row);
        return value === null
          ? { known: acc.known, missing: acc.missing + 1 }
          : { known: acc.known + value, missing: acc.missing };
      },
      { known: 0, missing: 0 },
    );

    return (
      <>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('lista_conteggio', {
            shown: numberFormat.format(slowRows.length),
            total: numberFormat.format(slowTotal ?? slowRows.length),
          })}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {valueTotal.missing > 0
            ? t('lista_valore_parziale', {
                amount: money(valueTotal.known),
                missing: numberFormat.format(valueTotal.missing),
              })
            : t('lista_valore', { amount: money(valueTotal.known) })}
        </Typography>
        <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('col_vino')}</TableCell>
                <TableCell align="right">{t('col_bottiglie')}</TableCell>
                <TableCell align="right">{t('col_valore')}</TableCell>
                <TableCell align="right">{t('col_eta')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slowRows.map((row) => (
                <TableRow key={row.sku}>
                  <TableCell>
                    <Box>
                      <Link
                        component={RouterLink}
                        href={paths.admin.products.details(row.sku)}
                        variant="body2"
                        sx={{ fontWeight: 'fontWeightMedium' }}
                      >
                        {row.full_name || row.name}
                      </Link>
                      {row.producer_name && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: 'text.disabled' }}
                        >
                          {row.producer_name}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{count(Number(row.storage_quantity))}</TableCell>
                  <TableCell align="right">{money(valueAtCost(row))}</TableCell>
                  <TableCell align="right">
                    {row.stock_age
                      ? t('giorni', { days: count(Number(row.stock_age)) })
                      : NOT_AVAILABLE}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4">{t('titolo')}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('sottotitolo')}
        </Typography>
      </Box>

      {renderCards()}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <SubHeading>{t('lista_titolo')}</SubHeading>
          <StatsExportButton build={buildExport} disabled={!slowRows.length} />
        </Box>
        {renderList()}
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {t('metodo')}
      </Typography>
    </Box>
  );
}
